import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Sparkles, LayoutDashboard, Settings as SettingsIcon, LogOut, Menu, PenTool, Save, Bell, Shield, CreditCard, Coins } from 'lucide-react'
import { signOut, getUser, isProUser, updatePassword, getSubscriptionDetails, getSubscriptionStatus } from '../utils/auth'
import { isAdminUser } from '../utils/admin'
import FeedbackWidget from '../components/FeedbackWidget'
import TopUpModal from '../components/TopUpModal'
import UpgradeModal from '../components/UpgradeModal'
import { ExternalLink, AlertTriangle, Calendar, Clock } from 'lucide-react'

export default function SettingsPage() {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const [user, setUser] = useState(null)
    const [isPro, setIsPro] = useState(false)
    const [isAdmin, setIsAdmin] = useState(false)
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [activeTab, setActiveTab] = useState('general')
    const [saving, setSaving] = useState(false)
    const [message, setMessage] = useState('')
    const [showTopUpModal, setShowTopUpModal] = useState(false)
    const [showUpgradeModal, setShowUpgradeModal] = useState(false)

    // Settings state
    const [settings, setSettings] = useState({
        detection_sensitivity: 'medium',
        auto_highlight: true,
        email_reports: true,
        dark_mode: true,
    })

    // Password reset state
    const [newPassword, setNewPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')

    // Subscription state
    const [subscription, setSubscription] = useState(null)
    const [subscriptionStatus, setSubscriptionStatus] = useState({ status: 'free', daysRemaining: null })

    useEffect(() => {
        const fetchUser = async () => {
            const currentUser = await getUser()
            setUser(currentUser)
            if (currentUser) {
                const proStatus = await isProUser(currentUser.id)
                setIsPro(proStatus)
                setIsAdmin(isAdminUser(currentUser.email))

                // Fetch full subscription details
                const subDetails = await getSubscriptionDetails(currentUser.id, currentUser.email)
                setSubscription(subDetails)
                setSubscriptionStatus(getSubscriptionStatus(subDetails))
            }
        }
        fetchUser()

        // Check if coming from password reset
        if (searchParams.get('reset') === 'true') {
            setActiveTab('security')
        }
    }, [searchParams])

    const handleSignOut = async () => {
        await signOut()
        navigate('/')
    }

    const handleSaveSettings = async () => {
        setSaving(true)
        // In production, save to database
        await new Promise(resolve => setTimeout(resolve, 1000))
        setMessage('Settings saved successfully!')
        setSaving(false)
        setTimeout(() => setMessage(''), 3000)
    }

    const handlePasswordChange = async (e) => {
        e.preventDefault()
        if (newPassword !== confirmPassword) {
            setMessage('Passwords do not match')
            return
        }
        if (newPassword.length < 8) {
            setMessage('Password must be at least 8 characters')
            return
        }

        setSaving(true)
        const { error } = await updatePassword(newPassword)
        setSaving(false)

        if (error) {
            setMessage(error.message)
        } else {
            setMessage('Password updated successfully!')
            setNewPassword('')
            setConfirmPassword('')
        }
    }

    const tabs = [
        { id: 'general', label: 'General', icon: SettingsIcon },
        { id: 'notifications', label: 'Notifications', icon: Bell },
        { id: 'security', label: 'Security', icon: Shield },
        { id: 'billing', label: 'Billing', icon: CreditCard },
    ]

    return (
        <div className="min-h-screen bg-slate-950 flex">
            {/* Sidebar */}
            <aside
                className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 border-r border-white/10 transform transition-transform duration-300 lg:translate-x-0 lg:static
                    ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
            >
                <div className="flex flex-col h-full">
                    {/* Logo */}
                    <div className="flex items-center gap-2 px-6 py-5 border-b border-white/10">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-400 to-accent-500 flex items-center justify-center">
                            <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-xl font-bold text-white">InkSpot</span>
                    </div>

                    {/* Nav Links */}
                    <nav className="flex-grow px-4 py-6">
                        <ul className="space-y-2">
                            <li>
                                <Link
                                    to="/app"
                                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-white/60 hover:bg-white/5 hover:text-white transition-colors"
                                >
                                    <PenTool className="w-5 h-5" />
                                    Editor
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/dashboard"
                                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-white/60 hover:bg-white/5 hover:text-white transition-colors"
                                >
                                    <LayoutDashboard className="w-5 h-5" />
                                    Dashboard
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/settings"
                                    className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/10 text-white font-medium"
                                >
                                    <SettingsIcon className="w-5 h-5" />
                                    Settings
                                </Link>
                            </li>
                            {isAdmin && (
                                <li>
                                    <Link
                                        to="/admin"
                                        className="flex items-center gap-3 px-4 py-3 rounded-xl text-red-400/70 hover:bg-red-500/10 hover:text-red-400 transition-colors"
                                    >
                                        <Shield className="w-5 h-5" />
                                        Admin Panel
                                    </Link>
                                </li>
                            )}
                        </ul>
                    </nav>

                    {/* User Section */}
                    <div className="p-4 border-t border-white/10">
                        <button
                            onClick={handleSignOut}
                            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-white/60 hover:bg-white/5 hover:text-white transition-colors"
                        >
                            <LogOut className="w-5 h-5" />
                            Sign out
                        </button>
                    </div>
                </div>
            </aside>

            {/* Mobile Sidebar Overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Main Content */}
            <main className="flex-grow min-h-screen overflow-y-auto">
                {/* Mobile Header */}
                <header className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-white/10">
                    <button
                        onClick={() => setSidebarOpen(true)}
                        className="p-2 text-white/70 hover:text-white"
                    >
                        <Menu className="w-6 h-6" />
                    </button>
                    <span className="font-bold text-white">Settings</span>
                    <div className="w-10" />
                </header>

                {/* Settings Content */}
                <div className="p-4 lg:p-8">
                    <div className="max-w-4xl mx-auto">
                        {/* Page Header */}
                        <div className="mb-8">
                            <h1 className="text-2xl font-bold text-white mb-2">Settings</h1>
                            <p className="text-white/60">Manage your account and preferences.</p>
                        </div>

                        {/* Status Message */}
                        {message && (
                            <div className={`mb-6 p-4 rounded-xl ${message.includes('success')
                                ? 'bg-green-500/10 border border-green-500/30 text-green-400'
                                : 'bg-red-500/10 border border-red-500/30 text-red-400'
                                }`}>
                                {message}
                            </div>
                        )}

                        {/* Tabs */}
                        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-colors
                             ${activeTab === tab.id
                                            ? 'bg-primary-500/20 text-primary-400'
                                            : 'text-white/60 hover:bg-white/5 hover:text-white'}`}
                                >
                                    <tab.icon className="w-4 h-4" />
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {/* Tab Content */}
                        <div className="glass-card p-6">
                            {activeTab === 'general' && (
                                <div className="space-y-6">
                                    <h2 className="text-lg font-semibold text-white mb-4">General Settings</h2>

                                    <div className="flex items-center justify-between py-4 border-b border-white/10">
                                        <div>
                                            <p className="font-medium text-white">Detection Sensitivity</p>
                                            <p className="text-sm text-white/50">Adjust how strict cliché detection should be</p>
                                        </div>
                                        <select
                                            value={settings.detection_sensitivity}
                                            onChange={(e) => setSettings({ ...settings, detection_sensitivity: e.target.value })}
                                            className="px-4 py-3 rounded-xl bg-slate-800 border border-white/10 text-white focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all duration-200 outline-none w-40 cursor-pointer"
                                            style={{ colorScheme: 'dark' }}
                                        >
                                            <option value="low" className="bg-slate-800 text-white">Low</option>
                                            <option value="medium" className="bg-slate-800 text-white">Medium</option>
                                            <option value="high" className="bg-slate-800 text-white">High</option>
                                        </select>
                                    </div>

                                    <div className="flex items-center justify-between py-4 border-b border-white/10">
                                        <div>
                                            <p className="font-medium text-white">Auto-highlight</p>
                                            <p className="text-sm text-white/50">Automatically highlight detected clichés</p>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={settings.auto_highlight}
                                                onChange={(e) => setSettings({ ...settings, auto_highlight: e.target.checked })}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 rounded-full bg-white/20 peer-checked:bg-primary-500 transition-colors" />
                                            <div className="absolute left-0.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-white shadow-md transition-all duration-200 peer-checked:left-[22px]" />
                                        </label>
                                    </div>

                                    <button
                                        onClick={handleSaveSettings}
                                        disabled={saving}
                                        className="btn-primary mt-4"
                                    >
                                        <Save className="w-4 h-4 mr-2" />
                                        {saving ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>
                            )}

                            {activeTab === 'notifications' && (
                                <div className="space-y-6">
                                    <h2 className="text-lg font-semibold text-white mb-4">Notification Preferences</h2>

                                    <div className="flex items-center justify-between py-4 border-b border-white/10">
                                        <div>
                                            <p className="font-medium text-white">Weekly Reports</p>
                                            <p className="text-sm text-white/50">Receive weekly writing improvement summaries</p>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={settings.email_reports}
                                                onChange={(e) => setSettings({ ...settings, email_reports: e.target.checked })}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 rounded-full bg-white/20 peer-checked:bg-primary-500 transition-colors" />
                                            <div className="absolute left-0.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-white shadow-md transition-all duration-200 peer-checked:left-[22px]" />
                                        </label>
                                    </div>

                                    <button onClick={handleSaveSettings} disabled={saving} className="btn-primary mt-4">
                                        <Save className="w-4 h-4 mr-2" />
                                        {saving ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>
                            )}

                            {activeTab === 'security' && (
                                <div className="space-y-6">
                                    <h2 className="text-lg font-semibold text-white mb-4">Security Settings</h2>

                                    <form onSubmit={handlePasswordChange} className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-white/80 mb-2">New Password</label>
                                            <input
                                                type="password"
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                className="input-field w-full max-w-md"
                                                placeholder="Min. 8 characters"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-white/80 mb-2">Confirm Password</label>
                                            <input
                                                type="password"
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                className="input-field w-full max-w-md"
                                                placeholder="Re-enter password"
                                            />
                                        </div>
                                        <button type="submit" disabled={saving} className="btn-primary">
                                            <Save className="w-4 h-4 mr-2" />
                                            {saving ? 'Updating...' : 'Update Password'}
                                        </button>
                                    </form>
                                </div>
                            )}

                            {activeTab === 'billing' && (
                                <div className="space-y-6">
                                    <h2 className="text-lg font-semibold text-white mb-4">Billing & Subscription</h2>

                                    {/* Expiration Warning */}
                                    {subscriptionStatus.status === 'expiring' && (
                                        <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30 flex items-start gap-3">
                                            <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                                            <div>
                                                <p className="font-medium text-yellow-400">Subscription Expiring</p>
                                                <p className="text-sm text-yellow-400/70">
                                                    Your Pro access will end in {subscriptionStatus.daysRemaining} days.
                                                    <button onClick={() => setShowUpgradeModal(true)} className="underline ml-1">Renew now</button>
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Expired Warning */}
                                    {subscriptionStatus.status === 'expired' && (
                                        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-start gap-3">
                                            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                                            <div>
                                                <p className="font-medium text-red-400">Subscription Expired</p>
                                                <p className="text-sm text-red-400/70">
                                                    Your Pro subscription has expired. You've been downgraded to the Free plan.
                                                    <button onClick={() => setShowUpgradeModal(true)} className="underline ml-1">Resubscribe now</button>
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Past Due Warning */}
                                    {subscriptionStatus.status === 'past_due' && (
                                        <div className="p-4 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-start gap-3">
                                            <AlertTriangle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                                            <div>
                                                <p className="font-medium text-orange-400">Payment Past Due</p>
                                                <p className="text-sm text-orange-400/70">
                                                    Your last payment failed. Please update your payment method to keep Pro access.
                                                    <a href="https://www.paypal.com/myaccount/autopay" target="_blank" rel="noopener noreferrer" className="underline ml-1">Update payment</a>
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Suspended Warning */}
                                    {subscriptionStatus.status === 'suspended' && (
                                        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-start gap-3">
                                            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                                            <div>
                                                <p className="font-medium text-red-400">Subscription Suspended</p>
                                                <p className="text-sm text-red-400/70">
                                                    Your subscription has been suspended due to payment issues. Please contact support or
                                                    <a href="https://www.paypal.com/myaccount/autopay" target="_blank" rel="noopener noreferrer" className="underline ml-1">update your payment method</a>.
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Current Plan Card */}
                                    <div className="p-6 rounded-xl bg-white/5 border border-white/10">
                                        <div className="flex items-center justify-between mb-4">
                                            <div>
                                                <p className="font-medium text-white">Current Plan</p>
                                                <p className="text-2xl font-bold gradient-text">
                                                    {['expired', 'suspended'].includes(subscriptionStatus.status) && subscription
                                                        ? 'Free' : (isPro ? 'Pro' : 'Free')}
                                                </p>
                                            </div>
                                            {isPro || (subscription && !['expired', 'suspended'].includes(subscriptionStatus.status)) ? (
                                                <span className={`px-3 py-1 rounded-full text-sm ${
                                                    subscriptionStatus.status === 'active'
                                                        ? 'bg-green-500/20 text-green-400'
                                                        : subscriptionStatus.status === 'expiring'
                                                            ? 'bg-yellow-500/20 text-yellow-400'
                                                            : subscriptionStatus.status === 'past_due'
                                                                ? 'bg-orange-500/20 text-orange-400'
                                                                : 'bg-red-500/20 text-red-400'
                                                    }`}>
                                                    {subscriptionStatus.status === 'active' ? 'Active' :
                                                        subscriptionStatus.status === 'expiring' ? 'Expiring' :
                                                        subscriptionStatus.status === 'past_due' ? 'Past Due' :
                                                        subscriptionStatus.status === 'expired' ? 'Expired' :
                                                        subscriptionStatus.status === 'suspended' ? 'Suspended' : 'Inactive'}
                                                </span>
                                            ) : (
                                                <button onClick={() => setShowUpgradeModal(true)} className="btn-primary">
                                                    Upgrade to Pro
                                                </button>
                                            )}
                                        </div>

                                        {isPro && subscription && (
                                            <div className="space-y-3 pt-4 border-t border-white/10">
                                                {/* Billing Date */}
                                                <div className="flex items-center gap-3 text-sm">
                                                    <Calendar className="w-4 h-4 text-white/40" />
                                                    <span className="text-white/70">Next billing:</span>
                                                    <span className="text-white">
                                                        {subscription.next_billing_date
                                                            ? new Date(subscription.next_billing_date).toLocaleDateString('en-US', {
                                                                month: 'long', day: 'numeric', year: 'numeric'
                                                            })
                                                            : 'Not set'}
                                                    </span>
                                                </div>

                                                {/* Price */}
                                                <div className="flex items-center gap-3 text-sm">
                                                    <CreditCard className="w-4 h-4 text-white/40" />
                                                    <span className="text-white/70">Amount:</span>
                                                    <span className="text-white">
                                                        {subscription.billing_cycle === 'annual' ? '$1.50/month ($18/year)' : '$3.00/month'}
                                                    </span>
                                                </div>

                                                {/* Subscription Start */}
                                                <div className="flex items-center gap-3 text-sm">
                                                    <Clock className="w-4 h-4 text-white/40" />
                                                    <span className="text-white/70">Subscribed since:</span>
                                                    <span className="text-white">
                                                        {subscription.started_at
                                                            ? new Date(subscription.started_at).toLocaleDateString('en-US', {
                                                                month: 'long', day: 'numeric', year: 'numeric'
                                                            })
                                                            : 'Unknown'}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Manage Billing */}
                                    {isPro && (
                                        <div className="space-y-4">
                                            <a
                                                href="https://www.paypal.com/myaccount/autopay"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors text-white"
                                            >
                                                <ExternalLink className="w-4 h-4" />
                                                Manage Billing on PayPal
                                                <span className="text-white/50 text-sm ml-auto">View payment history & update payment method</span>
                                            </a>

                                            <button
                                                onClick={() => {
                                                    if (confirm('Are you sure you want to cancel your Pro subscription? You\'ll lose access to Pro features at the end of your billing period.')) {
                                                        window.open('https://www.paypal.com/myaccount/autopay', '_blank')
                                                    }
                                                }}
                                                className="text-sm text-red-400 hover:text-red-300"
                                            >
                                                Cancel subscription
                                            </button>
                                        </div>
                                    )}

                                    {!isPro && (
                                        <div className="p-6 rounded-xl bg-gradient-to-br from-primary-500/10 to-accent-500/10 border border-primary-500/20">
                                            <h3 className="font-semibold text-white mb-2">Upgrade to Pro</h3>
                                            <p className="text-white/60 text-sm mb-4">
                                                Unlock unlimited AI detection, 2000+ patterns, and priority support.
                                            </p>
                                            <button onClick={() => setShowUpgradeModal(true)} className="btn-primary">
                                                Upgrade for $3/month
                                            </button>
                                        </div>
                                    )}

                                    {/* Top Up Credits */}
                                    <div className="p-6 rounded-xl bg-white/5 border border-white/10">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center">
                                                    <Coins className="w-5 h-5 text-white" />
                                                </div>
                                                <div>
                                                    <h3 className="font-semibold text-white">Pay-as-you-go Credits</h3>
                                                    <p className="text-sm text-white/50">50,000 AI tokens for $3 — no commitment</p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => setShowTopUpModal(true)}
                                                className="px-4 py-2 rounded-xl bg-white/10 border border-white/10 text-white hover:bg-white/15 transition-colors font-medium text-sm flex items-center gap-2"
                                            >
                                                <Coins className="w-4 h-4" />
                                                Buy Credits
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>

            {/* Top Up Credits Modal */}
            <TopUpModal
                isOpen={showTopUpModal}
                onClose={() => setShowTopUpModal(false)}
                userEmail={user?.email}
            />

            {/* Upgrade Modal */}
            <UpgradeModal
                isOpen={showUpgradeModal}
                onClose={() => setShowUpgradeModal(false)}
            />

            {/* Feedback Widget */}
            <FeedbackWidget user={user} />
        </div>
    )
}
